import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  AppliedFilter,
  DefaultTab,
  ShipmentTab,
} from '@/pages/Shipments/ControlTower/types';

export type DefaultTabId = 'all_shipments' | 'active';
export type ActiveTabId = DefaultTabId | string;

interface ShipmentTabState {
  defaultTabs: DefaultTab[];
  customTabs: ShipmentTab[];
  activeTabId: ActiveTabId;
  /** Per-tab ad-hoc filters keyed by tab id. Persisted via backend PATCH. */
  additionalFiltersPerTab: Record<string, AppliedFilter[]>;
  isLoading: boolean;
  hasLoaded: boolean;
  error: string | null;
}

const initialState: ShipmentTabState = {
  defaultTabs: [],
  customTabs: [],
  activeTabId: 'all_shipments',
  additionalFiltersPerTab: {},
  isLoading: false,
  hasLoaded: false,
  error: null,
};

const shipmentTabSlice = createSlice({
  name: 'shipmentTab',
  initialState,
  reducers: {
    setDefaultTabs(state, action: PayloadAction<DefaultTab[]>) {
      state.defaultTabs = action.payload;
    },

    setCustomTabs(state, action: PayloadAction<ShipmentTab[]>) {
      state.customTabs = action.payload;
      // Sync additionalFiltersPerTab from loaded tabs
      action.payload.forEach((tab) => {
        state.additionalFiltersPerTab[tab.id] = tab.additionalFilters ?? [];
      });
    },

    setActiveTabId(state, action: PayloadAction<ActiveTabId>) {
      state.activeTabId = action.payload;
    },

    addTab(state, action: PayloadAction<ShipmentTab>) {
      state.customTabs.push(action.payload);
      state.additionalFiltersPerTab[action.payload.id] =
        action.payload.additionalFilters ?? [];
    },

    removeTab(state, action: PayloadAction<string>) {
      state.customTabs = state.customTabs.filter(
        (t) => t.id !== action.payload
      );
      delete state.additionalFiltersPerTab[action.payload];
      // If the removed tab was active, fall back to all_shipments
      if (state.activeTabId === action.payload) {
        state.activeTabId = 'all_shipments';
      }
    },

    updateTabOrder(state, action: PayloadAction<ShipmentTab[]>) {
      state.customTabs = action.payload;
    },

    setAdditionalFilters(
      state,
      action: PayloadAction<{ tabId: string; filters: AppliedFilter[] }>
    ) {
      const { tabId, filters } = action.payload;
      state.additionalFiltersPerTab[tabId] = filters;
      // Keep ShipmentTab.additionalFilters in sync
      const tab = state.customTabs.find((t) => t.id === tabId);
      if (tab) {
        tab.additionalFilters = filters;
      }
    },

    resetAdditionalFilters(state, action: PayloadAction<string>) {
      const tabId = action.payload;
      state.additionalFiltersPerTab[tabId] = [];
      const tab = state.customTabs.find((t) => t.id === tabId);
      if (tab) {
        tab.additionalFilters = [];
      }
    },

    updateTabFilterSet(
      state,
      action: PayloadAction<{
        tabId: string;
        savedFilterSet: ShipmentTab['savedFilterSet'];
      }>
    ) {
      const { tabId, savedFilterSet } = action.payload;
      const tab = state.customTabs.find((t) => t.id === tabId);
      if (tab) {
        tab.savedFilterSet = savedFilterSet;
      }
    },

    setTabsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
      if (!action.payload) {
        state.hasLoaded = true;
      }
    },

    setTabsError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const {
  setDefaultTabs,
  setCustomTabs,
  setActiveTabId,
  addTab,
  removeTab,
  updateTabOrder,
  setAdditionalFilters,
  resetAdditionalFilters,
  updateTabFilterSet,
  setTabsLoading,
  setTabsError,
} = shipmentTabSlice.actions;

export const shipmentTabReducer = shipmentTabSlice.reducer;
